import {Button, Form, message, Popconfirm, Select, Table, Tag, Tooltip} from "antd";
import React, {useEffect, useState} from "react";
import CreateAssetModal from "./utils/category-modal";
import {AssetCategoryFormData, AssetFormData} from "./type/asset-category.types";
import {
    createCategory,
    assetCategoryModalMapper,
    updateCategory,
    assetModalMapper,
    categoryList, createAsset, assetSearch, updateAsset, assetStatusUpdate
} from "./action/asset-category.action";
import AssetModal from "./utils/asset-modal";
import dayjs from "dayjs";
import Locale from "../../../core/locale/locale";
import {removeUnderScore} from "../../../utility/utility";
import {Asset_Status, STATUS_TRANSITIONS_RULES} from "../../../constant/constant";
import {Pencil, Plus, RefreshCcw} from "lucide-react";
import InfiniteScrollTable from "../../common/infinite-scroll-table/infinite-scroll-table";
import "../../../styles/component/asset/asset-category.scss";
import LoadingSkeleton from "../../common/loading-skeleton/loading-skeleton";

const initialAssetCategoryFormData: AssetCategoryFormData = {
    id: "",
    name: "",
    description: ""
};

const initialAssetFormData: AssetFormData = {
    assetCode: "",
    isReturnable: false,
    status: "IN_STOCK"
};

const initialState = {
    modal: {
        category: false,
        asset: false
    },
    data: {
        categories: [],
        assets: {} as Record<string, any[]>,
    },
    selected: {
        category: initialAssetCategoryFormData,
        asset: initialAssetFormData
    },
    editMode: false,
    expandedRowKeys: [] as string[],
    updateStatus: "",
}

const AssetCategory = () => {
    const [createAssetCategoryForm] = Form.useForm();
    const [createAssetForm] = Form.useForm();
    const [assetScrollControls, setAssetScrollControls] = useState<Record<string, {
        loading: boolean;
        hasNext: boolean;
        page: number;
    }>>({});
    const [categoryLoading, setCategoryLoading] = useState(true);
    const [assetLoading, setAssetLoading] = useState(true);

    const [state, setState] = useState(initialState)

    const handleCreateCategory = () => {
        createAssetCategoryForm.resetFields();
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                category: true
            },
            editMode: false
        }))
    }

    const handleCreateAsset = (record: AssetCategoryFormData) => {
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                asset: true,
            },
            selected: {
                ...prev.selected,
                category: record
            },
            editMode: false
        }))
    };

    useEffect(() => {
        fetchAssetCategory();
    }, [])

    async function fetchAssetCategory() {
        setCategoryLoading(true);
        try {
            const assetCategoryResponse = await categoryList();
            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    categories: assetCategoryResponse
                }
            }))
        } catch (error) {
            console.log("Getting error while fetching asset categories", error)
        } finally {
            setCategoryLoading(false);
        }
    }

    const handleCategoryReset = () => {
        createAssetCategoryForm.resetFields();
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                category: false
            },
            selected: {
                ...prev.selected,
                category: initialAssetCategoryFormData
            }
        }))
    }

    const handleAssetReset = () => {
        createAssetForm.resetFields();
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                asset: false,
            },
            selected: {
                ...prev.selected,
                asset: initialAssetFormData
            }
        }))
    }

    async function handleStatusUpdate(record: any) {
        const id = record.id;
        const categoryId = record?.assetCategory?.id;
        try {
            await assetStatusUpdate(id, state.updateStatus);
            const response = await assetSearch(undefined, undefined, {
                categoryId: categoryId,
            });

            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    assets: {
                        ...prev.data.assets,
                        [categoryId]: response
                    }
                }
            }))
            message.success("Status updated successfully!");
        } catch (e) {
            console.log("Error while updating status", e);
        }
    }

    const handleAssetCategorySubmit = async (formValues: AssetCategoryFormData) => {
        const payload = assetCategoryModalMapper(formValues);
        if (state.selected.category?.id) {
            await updateCategory(state.selected.category.id, payload);
            message.success("Asset category updated successfully!")
            fetchAssetCategory();
            handleCategoryReset();
        } else {
            await createCategory(payload);
            fetchAssetCategory();
            handleCategoryReset();
            message.success("Asset category created successfully!");
        }
    }

    const handleAssetSubmit = async (formValues: AssetFormData) => {
        const categoryId = state.selected.category.id;

        const payload = assetModalMapper({
            id: state.selected.asset.id,
            ...formValues
        });

        const updatedPayload = {...payload, categoryId: state.selected.category.id}

        if (state.selected.asset?.id) {
            await updateAsset(state.selected.asset.id, updatedPayload);
            setState(prev => ({
                ...prev,
                data: {
                    ...prev.data,
                    assets: {
                        ...prev.data.assets,
                        [categoryId]: (prev.data.assets[categoryId] || []).map(item =>
                            item.id === state.selected.asset.id
                                ? {...item, ...payload}
                                : item
                        )
                    }
                }
            }));

            message.success("Asset updated successfully!");
        } else {
            await createAsset(updatedPayload);

            const updatedAssets = await assetSearch(undefined, undefined, {
                categoryId,
            });

            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    assets: {
                        ...prev.data.assets,
                        [categoryId]: updatedAssets,
                    }
                }
            }))
            message.success("Asset created successfully!");
        }
        handleAssetReset();
    };

    function handleAssetCategoryUpdateModal(record: AssetCategoryFormData) {
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                category: true
            },
            selected: {
                ...prev.selected,
                category: record
            },
            editMode: true
        }))

        createAssetCategoryForm.setFieldsValue({
            id: record.id,
            name: record.name,
            description: record.description,
        });
    }

    function handleAssetUpdateModal(assetRecord: AssetFormData) {
        setState((prev) => ({
            ...prev,
            modal: {
                ...prev.modal,
                asset: true,
            },
            selected: {
                ...prev.selected,
                asset: assetRecord
            },
            editMode: true
        }))
        createAssetForm.setFieldsValue({
            ...assetRecord,
            isReturnable: assetRecord.isReturnable,
            purchaseDate: assetRecord.purchaseDate ? dayjs(assetRecord.purchaseDate) : null,
            warrantyDate: assetRecord.warrantyDate ? dayjs(assetRecord.warrantyDate) : null,
            manufacturingDate: assetRecord.manufacturingDate ? dayjs(assetRecord.manufacturingDate) : null,
        });
    }

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
        },
        {
            title: 'Actions',
            dataIndex: 'operation',
            width: '25%',
            render: (_: any, record: AssetCategoryFormData) => (
                <div className={'pn-table-actions'}>
                    <Tooltip title={'Add Asset'}>
                        <Plus className={'action-add'} onClick={() => handleCreateAsset(record)}/>
                    </Tooltip>
                    <Tooltip title={'Edit category'}>
                        <Pencil className={'action-edit'}
                                onClick={() => handleAssetCategoryUpdateModal(record)}/>
                    </Tooltip>
                </div>
            ),
        },
    ]

    const handleExpand = async (expanded: boolean, record: AssetCategoryFormData) => {
        const key = record.id;
        setState((prev) => ({
            ...prev,
            selected: {
                ...prev.selected,
                category: record
            }
        }))

        if (expanded) {
            setState((prev) => ({
                ...prev,
                expandedRowKeys: [key] // only one row open at a time
            }))

            const payload = {
                categoryId: record.id,
            };
            setAssetLoading(true);
            try {
                const response = await assetSearch(0, 10, payload);
                setState((prev) => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        assets: {
                            ...prev.data.assets,
                            [record.id]: response
                        }
                    }
                }))

                setAssetScrollControls(prev => ({
                    ...prev,
                    [record.id]: {
                        loading: false,
                        hasNext: response.length >= 10, //10 is  size of the paginated API response
                        page: 0
                    }
                }));
            } catch (error) {
                console.log("Error fetching assets", error);
            } finally {
                setAssetLoading(false);
            }
        } else {
            setState((prev) => ({
                ...prev,
                expandedRowKeys: []
            }))
        }
    };

    const expandedColumns = [
        {
            title: "Asset Code",
            dataIndex: "assetCode",
            key: "assetCode",
        },
        {
            title: "Brand Name",
            dataIndex: "brandName",
            key: "brandName",
        },
        {
            title: "Purchase Date",
            dataIndex: "purchaseDate",
            key: "purchaseDate",
            render: (_: any, record: AssetFormData) => {
                return record.purchaseDate ? dayjs(record.purchaseDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Purchase Cost",
            dataIndex: "purchaseCost",
            key: "purchaseCost",
        },
        {
            title: "Warranty Date",
            dataIndex: "warrantyDate",
            key: "warrantyDate",
            render: (_: any, record: AssetFormData) => {
                return record.warrantyDate ? dayjs(record.warrantyDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Manufacturing Date ",
            dataIndex: "manufacturingDate",
            key: "manufacturingDate",
            render: (_: any, record: AssetFormData) => {
                return record.manufacturingDate ? dayjs(record.manufacturingDate).format(Locale.displayDate) : "-"
            }
        },
        {
            title: "Returnable",
            dataIndex: "isReturnable",
            key: "isReturnable",
            render: (isReturnable: boolean) => (
                <p>
                    {isReturnable ? "Yes" : "No"}
                </p>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: AssetFormData) => {
                return <Tag className={`asset-status ${record.status}`}>
                    {removeUnderScore(record.status)}
                </Tag>
            }
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: AssetFormData) => {
                const allowedStatuses = STATUS_TRANSITIONS_RULES[record.status] || [];
                return (
                    <div className={'pn-table-actions'}>
                        <Tooltip title={'Edit Asset'}>
                            <Pencil className={'action-edit'} onClick={() => handleAssetUpdateModal(record)}/>
                        </Tooltip>

                        <Tooltip title={"Update Asset status"}>
                            <Popconfirm
                                title={"Select Asset status"}
                                onConfirm={() => handleStatusUpdate(record)}
                                disabled={record.status === "IN_USE" || record.status === "DISPOSED"}
                                description={
                                    <Select
                                        onChange={(value: any) =>
                                            setState((prev) => ({
                                                ...prev,
                                                updateStatus: value
                                            }))
                                        }
                                        placeholder={'Select Asset Status'}
                                    >
                                        {allowedStatuses.map((statusKey: any) => (
                                            <Select.Option value={statusKey} key={statusKey}>
                                                {Asset_Status[statusKey as keyof typeof Asset_Status]}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                }
                            >
                                <RefreshCcw
                                    size={16}
                                    className={`action-renew ${record.status === "IN_USE" || record.status === "DISPOSED" ? "action-disabled" : ""}`}
                                />
                            </Popconfirm>
                        </Tooltip>
                    </div>
                )
            }
        },
    ];

    async function loadMoreAssets(categoryId: string) {
        const controls = assetScrollControls[categoryId];

        if (!controls || controls.loading || !controls.hasNext) return;

        const nextPage = controls.page + 1;

        setAssetScrollControls(prev => ({
            ...prev,
            [categoryId]: {
                ...prev[categoryId],
                loading: true
            }
        }));

        try {
            const response = await assetSearch(nextPage, 10, {
                categoryId
            });

            setState((prev) => ({
                ...prev,
                data: {
                    ...prev.data,
                    assets: {
                        ...prev.data.assets,
                        [categoryId]: [
                            ...(prev.data.assets[categoryId] || []),
                            ...response
                        ]
                    }
                }
            }))

            setAssetScrollControls(prev => ({
                ...prev,
                [categoryId]: {
                    loading: false,
                    hasNext: response.length >= 10,
                    page: nextPage
                }
            }));
        } catch (error) {
            console.log("Error fetching assets", error);

            setAssetScrollControls(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId],
                    loading: false
                }
            }));
        }
    }

    const expandedRowRender = (record: AssetCategoryFormData) => {
        const categoryId = record.id;
        const controls = assetScrollControls[categoryId];
        return (
            <LoadingSkeleton loading={assetLoading}>
                <InfiniteScrollTable
                    className={'expanded-asset-list-table'}
                    data={categoryId ? state.data.assets[categoryId] || [] : []}
                    rowKey={"id"}
                    bordered={true}
                    columns={expandedColumns}
                    loading={controls?.loading ?? false}
                    hasNext={controls?.hasNext ?? false}
                    loadMore={() => loadMoreAssets(categoryId)}
                />
            </LoadingSkeleton>
        );
    };

    return (
        <div className={'asset-category-container'}>
            <div className={'asset-category-top-section'}>
                <h3 className={'asset-category-heading'}> Asset category</h3>
                <Button type={'primary'} className={'add-btn'} onClick={handleCreateCategory}><Plus/>New
                    Category</Button>
            </div>

            <div className={'create-asset-category-table'}>
                <LoadingSkeleton loading={categoryLoading}>
                    <Table
                        rowKey="id"
                        columns={columns}
                        expandable={{
                            expandedRowRender,
                            onExpand: handleExpand,
                            expandedRowKeys: state.expandedRowKeys
                        }}
                        dataSource={state.data.categories}
                        pagination={false}
                        bordered={true}
                    />
                </LoadingSkeleton>

            </div>

            <CreateAssetModal
                createCategoryModal={state.modal.category}
                fetchAssetCateorgy={fetchAssetCategory}
                handleAssetCategorySubmit={handleAssetCategorySubmit}
                createAssetCategoryForm={createAssetCategoryForm}
                handleCategoryReset={handleCategoryReset}
                editMode={state.editMode}
            />

            <AssetModal
                createAssetModal={state.modal.asset}
                editMode={state.editMode}
                createAssetForm={createAssetForm}
                handleAssetReset={handleAssetReset}
                handleAssetSubmit={handleAssetSubmit}
            />

        </div>
    )
}

export default AssetCategory;
